import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Rating,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AddCustomerDialog from "./AddCustomerDialog";
import MultiSelectAutocomplete from "./MultiSelectAutocomplete";
import { authAPI, ticketsAPI } from "../services/api";
import {
  addCustomer,
  loadAllReferenceData,
  selectBranches,
  selectCommunicationMethods,
  selectContactReasons,
  selectCustomers,
  selectDepartments,
  selectIsReferenceDataLoading,
  selectProducts,
  selectReferenceDataError,
  selectShortSubjects,
} from "../store/slices/referenceDataSlice";

const getIdsFromItems = (items, idKey) => {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.map((item) =>
    typeof item === "number" ? item : item[idKey] ?? item.id
  );
};

const mapTicketToFormData = (ticket) => ({
  title: ticket.title || "",
  message: ticket.message || "",
  customer: ticket.customer?.customer_id
    ? ticket.customer.customer_id.toString()
    : "",
  communication_method: ticket.communication_method?.method_id
    ? ticket.communication_method.method_id.toString()
    : "",
  contact_reason: ticket.contact_reason?.reason_id
    ? ticket.contact_reason.reason_id.toString()
    : "",
  branches: getIdsFromItems(ticket.branches, "branch_id"),
  products: getIdsFromItems(ticket.products, "product_id"),
  departments: getIdsFromItems(ticket.departments, "department_id"),
  customer_rating: ticket.customer_rating ?? null,
  priority: ticket.priority || "medium",
});

const EditTicketForm = ({ ticketId, onNavigate }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const communicationMethods = useSelector(selectCommunicationMethods);
  const contactReasons = useSelector(selectContactReasons);
  const shortSubjects = useSelector(selectShortSubjects);
  const branches = useSelector(selectBranches);
  const products = useSelector(selectProducts);
  const departments = useSelector(selectDepartments);
  const customers = useSelector(selectCustomers);
  const isReferenceDataLoading = useSelector(selectIsReferenceDataLoading);
  const referenceDataError = useSelector(selectReferenceDataError);
  const isRTL = i18n.language === "ar";

  const [ticket, setTicket] = useState(null);
  const [isLoadingTicket, setIsLoadingTicket] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [replyCount, setReplyCount] = useState(0);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    customer: "",
    communication_method: "",
    contact_reason: "",
    branches: [],
    products: [],
    departments: [],
    customer_rating: null,
    priority: "medium",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [customerSearchValue, setCustomerSearchValue] = useState("");
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState([]);
  const [newAttachments, setNewAttachments] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const titleOptions = useMemo(
    () => shortSubjects.map((s) => (isRTL ? s.name_ar : s.name_en)),
    [shortSubjects, isRTL]
  );

  const getReferenceLabel = (item) =>
    (isRTL ? item.name_ar : item.name_en) || item.name_ar;

  useEffect(() => {
    if (
      customers.length === 0 &&
      !isReferenceDataLoading &&
      !referenceDataError
    ) {
      dispatch(loadAllReferenceData());
    }
  }, [customers.length, isReferenceDataLoading, referenceDataError, dispatch]);

  useEffect(() => {
    const loadTicket = async () => {
      if (!ticketId) {
        return;
      }
      try {
        setIsLoadingTicket(true);
        setLoadError(null);
        const [ticketData, replies] = await Promise.all([
          ticketsAPI.getTicket(ticketId),
          ticketsAPI.getTicketReplies(ticketId),
        ]);
        setTicket(ticketData);
        setReplyCount(Array.isArray(replies) ? replies.length : 0);
        setFormData(mapTicketToFormData(ticketData));
        setExistingAttachments(ticketData.attachments || []);
        if (ticketData.customer?.name) {
          setCustomerSearchValue(ticketData.customer.name);
        }
      } catch (error) {
        console.error("Error loading ticket for edit:", error);
        setLoadError(t("editTicket.loadError"));
      } finally {
        setIsLoadingTicket(false);
      }
    };

    loadTicket();
  }, [ticketId, t]);

  useEffect(() => {
    if (formData.customer && customers.length > 0) {
      const selectedCustomer = customers.find(
        (c) => c.customer_id.toString() === formData.customer
      );
      if (selectedCustomer) {
        setCustomerSearchValue(selectedCustomer.name);
      }
    }
  }, [formData.customer, customers]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field] || errors.general) {
      setErrors((prev) => ({ ...prev, [field]: "", general: "" }));
    }
  };

  const handleDepartmentChange = (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      departments: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleCustomerSearch = (event, searchValue) => {
    setCustomerSearchValue(searchValue);
  };

  const handleCustomerSelect = (event, selectedCustomer) => {
    if (selectedCustomer) {
      setFormData((prev) => ({
        ...prev,
        customer: selectedCustomer.customer_id.toString(),
      }));
      setCustomerSearchValue(selectedCustomer.name);
    } else {
      setFormData((prev) => ({ ...prev, customer: "" }));
      setCustomerSearchValue("");
    }
    if (errors.customer || errors.general) {
      setErrors((prev) => ({ ...prev, customer: "", general: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = t("createTicket.titleRequired");
    }
    if (!formData.message.trim()) {
      newErrors.message = t("createTicket.messageRequired");
    }
    if (!formData.communication_method) {
      newErrors.communication_method = t(
        "createTicket.communicationMethodRequired"
      );
    }
    if (!formData.contact_reason) {
      newErrors.contact_reason = t("createTicket.contactReasonRequired");
    }
    if (formData.departments.length === 0) {
      newErrors.departments = t("createTicket.departmentsRequired");
    }
    if (!formData.priority) {
      newErrors.priority = t("createTicket.departmentsRequired");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const needsOldTicketConfirm = () => {
    if (!ticket?.created_date) {
      return false;
    }
    const created = new Date(ticket.created_date);
    const hours = (Date.now() - created.getTime()) / (1000 * 60 * 60);
    return hours > 24 && replyCount > 0;
  };

  const performSave = async () => {
    setIsSubmitting(true);
    try {
      await authAPI.checkAuth();

      const ticketData = {
        title: formData.title,
        message: formData.message,
        departments: formData.departments.map((deptId) => parseInt(deptId, 10)),
        branches: formData.branches.map((branchId) => parseInt(branchId, 10)),
        products: formData.products.map((productId) => parseInt(productId, 10)),
        customer: formData.customer ? parseInt(formData.customer, 10) : null,
        communication_method: parseInt(formData.communication_method, 10),
        contact_reason: parseInt(formData.contact_reason, 10),
        customer_rating: formData.customer_rating,
        priority: formData.priority,
      };

      await ticketsAPI.updateTicket(ticketId, ticketData);

      for (const attachmentId of attachmentsToDelete) {
        await ticketsAPI.deleteTicketAttachment(attachmentId);
      }

      for (const attachment of newAttachments) {
        await ticketsAPI.addTicketAttachment(ticketId, attachment.file);
      }

      if (onNavigate) {
        onNavigate(`/tickets/${ticketId}`);
      }
    } catch (error) {
      console.error("Error updating ticket:", error);
      let errorMessage = t("editTicket.saveError");
      if (error.message?.includes("403")) {
        errorMessage = t("editTicket.permissionDenied");
      }
      setErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
      setConfirmDialogOpen(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (!user) {
      setErrors({ general: t("editTicket.loginRequired") });
      return;
    }
    if (isReferenceDataLoading) {
      setErrors({ general: t("editTicket.referenceDataLoading") });
      return;
    }
    if (referenceDataError) {
      setErrors({ general: t("editTicket.referenceDataError") });
      return;
    }
    if (needsOldTicketConfirm()) {
      setConfirmDialogOpen(true);
      return;
    }
    await performSave();
  };

  const handleCancel = () => {
    if (onNavigate) {
      onNavigate(`/tickets/${ticketId}`);
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const added = files.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
    }));
    setNewAttachments((prev) => [...prev, ...added]);
    event.target.value = "";
  };

  const handleRemoveNewAttachment = (attachmentId) => {
    setNewAttachments((prev) => prev.filter((att) => att.id !== attachmentId));
  };

  const handleRemoveExistingAttachment = (attachment) => {
    const id = attachment.attachment_id ?? attachment.id;
    setAttachmentsToDelete((prev) => [...prev, id]);
    setExistingAttachments((prev) =>
      prev.filter((att) => (att.attachment_id ?? att.id) !== id)
    );
  };

  const handleCustomerAdded = (newCustomer) => {
    dispatch(addCustomer(newCustomer));
    setFormData((prev) => ({
      ...prev,
      customer: newCustomer.customer_id.toString(),
    }));
    setCustomerSearchValue(newCustomer.name);
  };

  if (isLoadingTicket) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
        <Button onClick={handleCancel}>{t("common.cancel")}</Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 0.5, sm: 1, md: 1.5 },
        maxWidth: "100%",
        width: "100%",
      }}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: "100%",
          width: "100%",
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          align="center"
          sx={{
            fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
            mb: 2,
            fontWeight: "bold",
          }}
        >
          {t("editTicket.title", { id: ticketId })}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {errors.general && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.general}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ width: "100%" }}>
            <Box sx={{ mb: 3, width: "100%" }}>
              <Autocomplete
                freeSolo
                fullWidth
                options={titleOptions}
                value={formData.title}
                onInputChange={(event, newValue) => {
                  setFormData((prev) => ({ ...prev, title: newValue || "" }));
                  if (errors.title) {
                    setErrors((prev) => ({ ...prev, title: "" }));
                  }
                }}
                onChange={(event, newValue) => {
                  const title =
                    typeof newValue === "string" ? newValue : newValue || "";
                  setFormData((prev) => ({ ...prev, title }));
                  if (errors.title) {
                    setErrors((prev) => ({ ...prev, title: "" }));
                  }
                }}
                disabled={isReferenceDataLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("createTicket.titleLabel")}
                    error={!!errors.title}
                    helperText={errors.title}
                    required
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontSize: { xs: "16px", sm: "16px" },
                        minHeight: { xs: "56px", sm: "56px" },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "16px", sm: "16px" },
                      },
                    }}
                  />
                )}
              />
            </Box>

            <Box sx={{ mb: 3, width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <Autocomplete
                    fullWidth
                    options={customers}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return `${option.name} - ${option.contact}`;
                    }}
                    value={
                      customers.find(
                        (c) => c.customer_id.toString() === formData.customer
                      ) || null
                    }
                    onChange={handleCustomerSelect}
                    onInputChange={handleCustomerSearch}
                    inputValue={customerSearchValue}
                    disabled={isReferenceDataLoading}
                    noOptionsText={t("tickets.noResults")}
                    isOptionEqualToValue={(option, value) =>
                      option.customer_id === value.customer_id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("createTicket.customerLabel")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontSize: { xs: "16px", sm: "16px" },
                            minHeight: { xs: "56px", sm: "56px" },
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: { xs: "16px", sm: "16px" },
                          },
                        }}
                      />
                    )}
                  />
                  <IconButton
                    onClick={() => setAddCustomerDialogOpen(true)}
                    disabled={isReferenceDataLoading}
                    color="primary"
                    sx={{
                      mt: 1,
                      height: "56px",
                      width: "56px",
                      border: "1px solid",
                      borderColor: "primary.main",
                      borderRadius: 1,
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    minWidth: { xs: "100%", md: "300px" },
                    height: "56px",
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t("createTicket.customerRatingLabel")}:
                  </Typography>
                  <Rating
                    value={formData.customer_rating}
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        customer_rating: newValue,
                      }));
                    }}
                    size="medium"
                  />
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: { xs: 2, lg: 2 },
                mb: 3,
                width: "100%",
              }}
            >
              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 25%" } }}>
                <FormControl fullWidth error={!!errors.communication_method}>
                  <InputLabel>
                    {t("createTicket.communicationMethodLabel")}
                  </InputLabel>
                  <Select
                    value={formData.communication_method}
                    onChange={handleInputChange("communication_method")}
                    label={t("createTicket.communicationMethodLabel")}
                    required
                    disabled={isReferenceDataLoading}
                  >
                    {communicationMethods.map((method) => (
                      <MenuItem key={method.method_id} value={method.method_id}>
                        {isRTL ? method.name_ar : method.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.communication_method && (
                    <FormHelperText>
                      {errors.communication_method}
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>

              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 25%" } }}>
                <FormControl fullWidth error={!!errors.contact_reason}>
                  <InputLabel>{t("createTicket.contactReasonLabel")}</InputLabel>
                  <Select
                    value={formData.contact_reason}
                    onChange={handleInputChange("contact_reason")}
                    label={t("createTicket.contactReasonLabel")}
                    required
                    disabled={isReferenceDataLoading}
                  >
                    {contactReasons.map((reason) => (
                      <MenuItem key={reason.reason_id} value={reason.reason_id}>
                        {isRTL ? reason.name_ar : reason.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.contact_reason && (
                    <FormHelperText>{errors.contact_reason}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 25%" } }}>
                <MultiSelectAutocomplete
                  label={t("createTicket.branchLabel")}
                  placeholder={t("createTicket.selectBranch")}
                  options={branches}
                  value={formData.branches}
                  onChange={(branchIds) =>
                    setFormData((prev) => ({ ...prev, branches: branchIds }))
                  }
                  getOptionLabel={getReferenceLabel}
                  getOptionValue={(branch) => branch.branch_id}
                  disabled={isReferenceDataLoading}
                  isRTL={isRTL}
                />
              </Box>

              <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 25%" } }}>
                <MultiSelectAutocomplete
                  label={t("createTicket.productLabel")}
                  placeholder={t("createTicket.selectProduct")}
                  options={products}
                  value={formData.products}
                  onChange={(productIds) =>
                    setFormData((prev) => ({ ...prev, products: productIds }))
                  }
                  getOptionLabel={getReferenceLabel}
                  getOptionValue={(product) => product.product_id}
                  disabled={isReferenceDataLoading}
                  isRTL={isRTL}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: { xs: 2, lg: 2 },
                mb: 3,
                width: "100%",
              }}
            >
              <Box sx={{ flex: { xs: "1 1 100%", lg: "8" } }}>
                <FormControl fullWidth error={!!errors.departments}>
                  <InputLabel>{t("createTicket.departmentsLabel")}</InputLabel>
                  <Select
                    multiple
                    value={formData.departments}
                    onChange={handleDepartmentChange}
                    input={
                      <OutlinedInput
                        label={t("createTicket.departmentsLabel")}
                      />
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const dept = departments.find(
                            (d) => d.department_id === value
                          );
                          return (
                            <Chip
                              key={value}
                              label={
                                dept
                                  ? isRTL
                                    ? dept.name_ar
                                    : dept.name_en
                                  : value
                              }
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                    required
                    disabled={isReferenceDataLoading}
                  >
                    {departments.map((department) => (
                      <MenuItem
                        key={department.department_id}
                        value={department.department_id}
                      >
                        {isRTL ? department.name_ar : department.name_en}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.departments && (
                    <FormHelperText>{errors.departments}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              <Box sx={{ flex: { xs: "1 1 100%", lg: "4" } }}>
                <FormControl fullWidth error={!!errors.priority}>
                  <InputLabel>{t("common.priority")}</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={handleInputChange("priority")}
                    label={t("common.priority")}
                    required
                  >
                    <MenuItem value="low">{t("common.low")}</MenuItem>
                    <MenuItem value="medium">{t("common.medium")}</MenuItem>
                    <MenuItem value="high">{t("common.high")}</MenuItem>
                    <MenuItem value="urgent">{t("tickets.priority.urgent")}</MenuItem>
                  </Select>
                  {errors.priority && (
                    <FormHelperText>{errors.priority}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ mb: 3, width: "100%" }}>
              <TextField
                fullWidth
                multiline
                rows={12}
                label={t("createTicket.messageLabel")}
                value={formData.message}
                onChange={handleInputChange("message")}
                error={!!errors.message}
                helperText={errors.message}
                required
                variant="outlined"
              />
            </Box>

            <Box sx={{ mb: 3, width: "100%" }}>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
                {t("editTicket.attachments")}
              </Typography>

              {existingAttachments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t("editTicket.existingAttachments")} (
                    {existingAttachments.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {existingAttachments.map((attachment) => (
                      <Chip
                        key={attachment.attachment_id ?? attachment.id}
                        label={attachment.filename || attachment.name}
                        onDelete={() =>
                          handleRemoveExistingAttachment(attachment)
                        }
                        color="default"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <input
                  accept="image/*,video/*,.pdf,.doc,.docx,.txt,.xlsx,.xls"
                  style={{ display: "none" }}
                  id="edit-file-upload"
                  multiple
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="edit-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AddIcon />}
                    sx={{ borderStyle: "dashed", borderWidth: "2px" }}
                  >
                    {t("editTicket.addAttachments")}
                  </Button>
                </label>
              </Box>

              {newAttachments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t("editTicket.newAttachments")} ({newAttachments.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {newAttachments.map((attachment) => (
                      <Chip
                        key={attachment.id}
                        label={attachment.name}
                        onDelete={() => handleRemoveNewAttachment(attachment.id)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: { xs: "center", sm: "flex-end" },
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
                size="large"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                size="large"
              >
                {isSubmitting ? t("editTicket.saving") : t("editTicket.save")}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>

      <AddCustomerDialog
        open={addCustomerDialogOpen}
        onClose={() => setAddCustomerDialogOpen(false)}
        onCustomerAdded={handleCustomerAdded}
      />

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>{t("editTicket.confirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("editTicket.confirmOldWithReplies")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            {t("referencePages.no")}
          </Button>
          <Button onClick={performSave} variant="contained" disabled={isSubmitting}>
            {t("referencePages.yes")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditTicketForm;
